// PC 스킬 바(하단 가운데): 공격 J · 스킬 슬롯 6칸(현재 페이지, 슬롯 번호 1~6 순서) · 회피 K(액션식)/Shift(PC RPG식) · 각성 Space. 왼쪽 끝 칩 = 쪽 표시(1/2)·Tab·다음 스킬 Lv.
// 스킬: 피의 돌진 U · 칼날 폭풍 I · 붉은 낙화 O(처음부터) · 천공 올려베기 P(Lv5 자동 습득) · 진홍 초승달 L(Lv10 자동 습득). 옛 키는 항상 그 스킬이고, 슬롯 키(1~6·Alt+1~6·Tab)는 슬롯 배치(GameView.slots)를 따른다(docs/SKILL-TREE.md 11.3).
// 스킬은 skillArt.ts의 사각 원화 전체를 표시한다. 공격·회피·각성만 기존 링을 유지하며 터치와 buildSkillFace를 공유한다.
// 이미지가 실패하면 그 버튼만 직접 그린 SVG 아이콘 + 금테 원(CSS)으로 대체한다.
// 쿨타임 원형 스윕·남은 초·MP 부족 표시·준비 반짝임·눌림/거부 반응. 프레임마다 새 문자열을 만들지 않도록 스윕·초 문자열은 표로 만들어 두고 값이 바뀔 때만 DOM을 쓴다.
import { effectiveMpCost } from '../shared/gear';
import { DODGE, FLASH_SAFETY, KAEL_SKILL_SPECS_BY_JOB, LYRA_SKILL_SPECS_BY_JOB } from '../shared/config';
import { SLOTS_PER_PAGE, keySlot, slotBtn, slotsAll } from '../shared/skillSlots';
import { Ev, type GameEvent } from '../shared/events';
import { AwakenPhase, Btn, HeroAct, SKILL_BTN_ALL, SKILL_MAX, type BtnBit, type GameCommand, type GameView, type InputSink, type PlayableHeroId } from '../shared/types';
import { C, FONT, FONT_NUM, TEXT_OUTLINE, el, hudImg, type HudImage } from './dom';
import { PAGE_KEY_HINT, cellOfSkill, layoutKey, learnedMaskOf, nextSkillText, pageSwitchOpen, pageText, skillShown, slotsOf } from './slotsModel';
import { growthOf, skillSpecOf } from './skillTreeModel';
import { viewW } from '../shared/viewport';
import { GearCue, uniqueCueOf } from '../shared/gearCues';
import { PotionHud } from './potionHud';
import { LYRA_FACE_BG, LYRA_ICONS } from './lyraIcons';
import { mountSkillArt, skillIdOfBtn, type SkillArt } from './skillArt';
import { AWAKEN_SKILL_ID, skillIconPath } from './skillIcons';
import { assetUrl } from '../pwa/assetMap';
import { dodgeKeyLabel, keymap } from '../shared/keymap';

// ── 아이콘(64×64, 흰 실루엣 + 어두운 외곽선) ─────────────────────────
const SVG_OPEN = '<svg viewBox="0 0 64 64" width="100%" height="100%" fill="#fff" stroke="#2a0810" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" style="display:block;filter:drop-shadow(0 2px 1px rgba(0,0,0,.55))">';
const SVG_CLOSE = '</svg>';
const STORM_BLADE = '<path d="M32 32 C37 17 51 9 61 15 C49 16 41 24 37 37 Z"/>';
/** 버튼 비트 → SVG 문자열. HUD 이미지가 실패했을 때의 대체 아이콘(PC 바·터치 공용) */
export const SKILL_ICONS: Readonly<Record<number, string>> = {
  // 대검(대각선)
  [Btn.Attack]: SVG_OPEN +
    '<g transform="rotate(45 32 32)"><path d="M32 3 L37.5 11 L37.5 41 L26.5 41 L26.5 11 Z"/><path d="M32 12 V38" stroke="#7a1a24" stroke-width="1.6" fill="none"/>' +
    '<rect x="18" y="41" width="28" height="5.5" rx="2"/><rect x="29" y="46.5" width="6" height="10" rx="1.5"/><circle cx="32" cy="59" r="3.6"/></g>' + SVG_CLOSE,
  // 피의 돌진: 앞으로 찌르는 칼날 + 뒤로 남는 속도선
  [Btn.Skill1]: SVG_OPEN +
    '<g fill="none" stroke="#fff" stroke-width="3.2" opacity=".85"><path d="M3 15 H26"/><path d="M7 49 H26"/><path d="M2 24 H13"/><path d="M2 40 H13"/></g>' +
    '<path d="M61 32 L52 26 L25 26 L25 38 L52 38 Z"/><path d="M28 32 H50" stroke="#7a1a24" stroke-width="1.6" fill="none"/>' +
    '<rect x="19.5" y="19" width="5.5" height="26" rx="2"/><rect x="10" y="29" width="9.5" height="6" rx="1.5"/>' + SVG_CLOSE,
  // 칼날 폭풍: 세 갈래 초승달 칼날 회오리
  [Btn.Skill2]: SVG_OPEN + STORM_BLADE +
    '<g transform="rotate(120 32 32)">' + STORM_BLADE + '</g><g transform="rotate(240 32 32)">' + STORM_BLADE + '</g>' +
    '<circle cx="32" cy="32" r="6.5"/><circle cx="32" cy="32" r="2.6" fill="#7a1a24" stroke="none"/>' + SVG_CLOSE,
  // 붉은 낙화: 아래로 꽂히는 칼 + 땅 충격 파편
  [Btn.Skill3]: SVG_OPEN +
    '<path d="M32 50 L26.5 42 L26.5 15 L37.5 15 L37.5 42 Z"/><path d="M32 18 V40" stroke="#7a1a24" stroke-width="1.6" fill="none"/>' +
    '<rect x="19" y="9.5" width="26" height="5.5" rx="2"/><rect x="29" y="2" width="6" height="7.5" rx="1.5"/>' +
    '<g fill="none" stroke="#fff" stroke-width="3"><path d="M8 59 Q32 49 56 59"/><path d="M22 50 L12 44"/><path d="M42 50 L52 44"/><path d="M18 56 L6 53"/><path d="M46 56 L58 53"/></g>' + SVG_CLOSE,
  // 진홍 초승달: 앞으로 날아가는 초승달 검기 + 속도선
  [Btn.Skill4]: SVG_OPEN +
    '<g fill="none" stroke="#fff" stroke-width="3" opacity=".8"><path d="M3 22 H14"/><path d="M2 32 H11"/><path d="M5 42 H14"/></g>' +
    '<path d="M56 6 C33 7 16 22 16 42 C16 47 17 51 19 55 C22 38 34 27 56 21 C59 16 59 11 56 6 Z"/><path d="M24 47 C31 37 41 30 53 26" stroke="#7a1a24" stroke-width="1.8" fill="none"/>' + SVG_CLOSE,
  // 천공 올려베기: 아래에서 위로 올려베는 칼날 + 솟는 파편(런처)
  [Btn.Skill5]: SVG_OPEN +
    '<g fill="none" stroke="#fff" stroke-width="3" opacity=".8"><path d="M12 56 H26"/><path d="M38 56 H52"/><path d="M8 47 L18 41"/><path d="M56 47 L46 41"/></g>' +
    '<path d="M32 4 L40 14 L40 42 L24 42 L24 14 Z"/><path d="M32 12 V38" stroke="#7a1a24" stroke-width="1.6" fill="none"/>' +
    '<rect x="15" y="42" width="34" height="5.5" rx="2"/><rect x="29" y="47.5" width="6" height="9" rx="1.5"/>' + SVG_CLOSE,
  // 회피: 겹친 화살촉 + 잔상선
  [Btn.Dodge]: SVG_OPEN +
    '<g fill="none" stroke="#fff" stroke-width="3" opacity=".7"><path d="M5 24 H15"/><path d="M3 32 H15"/><path d="M5 40 H15"/></g>' +
    '<path d="M20 15 L36 32 L20 49 L26 49 L42 32 L26 15 Z" opacity=".75"/><path d="M34 15 L50 32 L34 49 L41 49 L58 32 L41 15 Z"/>' + SVG_CLOSE,
  // 각성: 불꽃 문장 + 붉은 눈
  [Btn.Awaken]: SVG_OPEN +
    '<path d="M32 3 C41 15 51 21 49 37 C48 50 40 59 32 59 C24 59 16 50 15 37 C14 26 22 21 25 11 C29 19 29 24 32 27 C35 20 35 12 32 3 Z"/>' +
    '<path d="M21 43 Q32 33 43 43 Q32 52 21 43 Z" fill="#7a0b18" stroke="#2a0810"/><circle cx="32" cy="43" r="3.4" fill="#ffd36a" stroke="none"/>' + SVG_CLOSE,
};

// ── 링 틀 측정값(qa/m1/hud3/tools/measure.py): 원본 크기, 투명 구멍 중심(가로·세로 비율), 구멍 지름(가로 비율) ──
export interface RingSpec {
  readonly img: HudImage;
  readonly w: number;
  readonly h: number;
  readonly cx: number;
  readonly cy: number;
  readonly hole: number;
}
export const RING_SKILL: RingSpec = { img: 'ringSkill', w: 256, h: 252, cx: 0.4966, cy: 0.4929, hole: 0.699 };
export const RING_ATTACK: RingSpec = { img: 'ringAttack', w: 320, h: 318, cx: 0.4971, cy: 0.487, hole: 0.62 };
export const RING_AWAKEN: RingSpec = { img: 'ringAwaken', w: 320, h: 308, cx: 0.4972, cy: 0.4826, hole: 0.497 };
/** 아이콘 원을 구멍보다 조금 크게 깔아 가장자리를 금테 밑으로 숨긴다 */
const ICON_OVER = 1.08;

const RING_ACTIVE: RingSpec = { img: 'ringSkill', w: 256, h: 256, cx: .5, cy: .5, hole: .96 };
export function ringOf(bit: number): RingSpec {
  if (skillIdOfBtn(bit) > 0) return RING_ACTIVE;
  return bit === Btn.Attack ? RING_ATTACK : bit === Btn.Awaken ? RING_AWAKEN : RING_SKILL;
}
/** 비스킬 조작의 기존 HUD 원화. 액티브 스킬 주소는 skillArt.ts에서만 정한다. */
const ICON_OF: Readonly<Record<number, HudImage | undefined>> = {
  [Btn.Attack]: 'attack', [Btn.Dodge]: 'dodge', [Btn.Awaken]: 'awaken',
};
/** 이미지 실패 시 버튼 바탕(이전 CSS 모양) */
const FALLBACK_BG: Readonly<Record<number, string>> = {
  [Btn.Attack]: 'radial-gradient(circle at 35% 30%,#7a1d2a,#2a0a12 72%)',
  [Btn.Skill1]: 'radial-gradient(circle at 35% 30%,#d0343f,#6a0f1c 62%,#2a0610)',
  [Btn.Skill2]: 'radial-gradient(circle at 35% 30%,#d0343f,#6a0f1c 62%,#2a0610)',
  [Btn.Skill3]: 'radial-gradient(circle at 35% 30%,#d0343f,#6a0f1c 62%,#2a0610)',
  [Btn.Skill4]: 'radial-gradient(circle at 35% 30%,#e0303f,#7a0f20 62%,#2a0610)',
  [Btn.Skill5]: 'radial-gradient(circle at 35% 30%,#e8742a,#7a2410 62%,#2a0c06)',
  [Btn.Dodge]: 'radial-gradient(circle at 35% 30%,#4f6290,#1a2238 70%)',
  [Btn.Awaken]: 'radial-gradient(circle at 35% 30%,#ff8a5a,#a0101f 58%,#3a0610)',
};

/** 링 + 원형 아이콘 버튼 한 개의 요소들 */
export interface SkillFace {
  /** 크기 기준 상자(가로를 px로 정하면 세로는 링 비율) */
  root: HTMLDivElement;
  /** 구멍 자리의 원형 아이콘(어둡게·맥박은 여기에만: 금테는 밝게 남긴다) */
  face: HTMLDivElement;
  sweep: HTMLDivElement;
  cdText: HTMLDivElement;
  /** 준비 반짝임 링 */
  flash: HTMLDivElement;
  /** 캔슬 가능 표시(LINK): 맥동하는 금색 바깥 고리와 아래 CANCEL 꼬리표. 평소 숨김, setCancelMark로만 켠다 */
  cancel: HTMLDivElement;
  cancelTag: HTMLDivElement;
  /** 칼날의 박자(전설 고유, 치명타 재사용 감소) 시계 바늘 반짝임(c017). 아이콘 원 안에서 한 바퀴 돈다 */
  glint: HTMLDivElement;
  /** 마지막 반짝임 시각(ms, 최소 간격 GLINT_GAP_MS) */
  glintAt: number;
  /** 카엘 원화 + SVG 대체 아이콘 묶음(영웅이 라이라면 숨김) */
  kaelLayer: HTMLDivElement;
  /** 라이라 절차 아이콘(청록+은). 라이라 글리프가 없는 버튼(회피)은 null */
  lyraLayer: HTMLDivElement | null;
  /** 영웅별 바탕색을 되돌리기 위한 카엘 바탕 */
  kaelBg: string;
  skill: number;
  art: SkillArt | null;
}

/** 구멍 자리(root 기준 퍼센트) */
function holeBox(r: RingSpec, scale: number): string {
  const d = r.hole * scale;
  const dh = d * (r.w / r.h);
  return 'left:' + ((r.cx - d / 2) * 100).toFixed(2) + '%;top:' + ((r.cy - dh / 2) * 100).toFixed(2) + '%;width:' + (d * 100).toFixed(2) + '%;height:' + (dh * 100).toFixed(2) + '%;';
}

/** 아이콘 원 + 링 틀. css는 root에 덧붙인다(위치·크기는 호출자가 정한다) */
export function buildSkillFace(parent: HTMLElement, bit: BtnBit, css: string): SkillFace {
  ensureStyle();
  const ring = ringOf(bit);
  const skill = skillIdOfBtn(bit);
  const root = el('div', 'position:relative;aspect-ratio:' + ring.w + '/' + ring.h + ';' + css, parent);
  const face = el('div', 'position:absolute;' + holeBox(ring, ICON_OVER) + 'border-radius:50%;overflow:hidden;box-sizing:border-box;background:' + FALLBACK_BG[bit] + ';pointer-events:none;', root);
  root.className = skill > 0 ? 'dh-skill-face active' : 'dh-skill-face';
  if (skill > 0) { face.style.cssText = 'position:absolute;inset:0;box-sizing:border-box;border:1px solid #bca46b;border-radius:7px;overflow:hidden;background:#071321;pointer-events:none;'; }
  const kaelLayer = el('div', 'position:absolute;inset:0;pointer-events:none;', face);
  let art: SkillArt | null = null;
  if (skill > 0) art = mountSkillArt(kaelLayer, 'kael', skill, SKILL_ICONS[bit] ?? '');
  else {
    const svg = el('div', 'position:absolute;inset:16%;display:none;pointer-events:none;', kaelLayer);
    svg.innerHTML = SKILL_ICONS[bit] ?? '';
    const icon = ICON_OF[bit];
    if (icon) hudImg(icon, 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;', kaelLayer, () => { svg.style.display = 'block'; });
    else svg.style.display = 'block';
  }
  // 라이라의 비스킬 조작(공격·각성)만 기존 글리프를 유지한다. 액티브는 영웅별 원화를 사용한다.
  const lyraSvg = LYRA_ICONS[bit];
  let lyraLayer: HTMLDivElement | null = null;
  if (lyraSvg && skill === 0) {
    lyraLayer = el('div', 'position:absolute;inset:0;display:none;pointer-events:none;background:' + LYRA_FACE_BG + ';', face);
    el('div', 'position:absolute;inset:14%;pointer-events:none;', lyraLayer).innerHTML = lyraSvg;
    // 라이라 각성(폭풍 화살비): 2026-10-27 스킬 아이콘 회차 그림을 글리프 위에 덮는다(그림이 없으면 글리프 그대로)
    const aw = bit === Btn.Awaken ? skillIconPath('lyra', AWAKEN_SKILL_ID) : null;
    if (aw) {
      const im = el('img', 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none;', lyraLayer) as HTMLImageElement;
      im.alt = ''; im.draggable = false; im.decoding = 'async';
      im.dataset.testid = 'lyra-awaken-art';
      im.addEventListener('error', () => { im.remove(); });
      im.src = assetUrl(aw);
    }
  }
  // 가장자리를 살짝 어둡게 해 아이콘이 링 안으로 들어가 보이게
  el('div', 'position:absolute;inset:0;border-radius:50%;background:radial-gradient(circle,rgba(0,0,0,0) 58%,rgba(0,0,0,.5) 100%);pointer-events:none;', face);
  const sweep = el('div', 'position:absolute;inset:0;border-radius:50%;pointer-events:none;', face);
  const cdText = el('div', 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font:900 20px ' + FONT_NUM + ';font-style:italic;color:#fff;-webkit-text-stroke:1.5px #10162a;text-shadow:0 2px 3px #000;pointer-events:none;', face, '');
  if (skill === 0) hudImg(ring.img, 'position:absolute;inset:0;width:100%;height:100%;', root, () => {
    face.style.border = (bit === Btn.Attack ? 4 : 3) + 'px solid ' + C.gold;
    face.style.boxShadow = '0 3px 8px rgba(0,0,0,.5)';
  });
  const flash = el('div', 'position:absolute;' + holeBox(ring, 1.12) + 'border-radius:50%;border:2px solid #fff;box-shadow:0 0 16px 5px rgba(255,236,170,.95),inset 0 0 10px rgba(255,255,255,.75);opacity:0;pointer-events:none;box-sizing:border-box;', root);
  // 캔슬 표시(LINK): 금색 고리는 링 틀 바깥으로, 꼬리표는 링 아래쪽 가장자리에 걸친다(그 아래 줄이 버튼 이름)
  const cancel = el('div', 'position:absolute;' + holeBox(ring, 1.2) + 'box-sizing:border-box;border-radius:50%;border:3px solid #ffd45a;display:none;pointer-events:none;z-index:1;animation:' + CANCEL_ANIM + ';', root);
  const cancelTag = el('div', CANCEL_TAG_CSS, root, CANCEL_TEXT);
  cancel.className = 'dh-sk-cancel';
  // c017 시계 바늘 반짝임: 아이콘 구멍 크기 원판에 12시에서 시작하는 얇은 금백 쐐기 + 뒤로 흐려지는 꼬리(원판 모양은 border-radius가 자른다). 평소 투명
  const glint = el('div', 'position:absolute;' + holeBox(ring, 1.02) + 'border-radius:50%;pointer-events:none;opacity:0;box-sizing:border-box;' +
    'background:conic-gradient(from -24deg,rgba(255,236,170,0) 0deg,rgba(255,255,244,1) 20deg,rgba(255,232,160,.9) 26deg,rgba(255,196,90,.45) 60deg,rgba(255,170,60,.12) 120deg,rgba(255,170,60,0) 170deg,rgba(255,170,60,0) 360deg);' +
    'box-shadow:inset 0 0 0 3px rgba(255,226,140,.95),inset 0 0 12px rgba(255,214,120,.6),0 0 14px 4px rgba(255,200,90,.7);', root);
  if (skill > 0) {
    sweep.style.borderRadius = '6px'; flash.style.borderRadius = cancel.style.borderRadius = glint.style.borderRadius = '7px';
    flash.style.inset = cancel.style.inset = glint.style.inset = '0';
  }
  glint.dataset.fx = 'glint';
  return { root, face, sweep, cdText, flash, cancel, cancelTag, glint, glintAt: -1e9, kaelLayer, lyraLayer, kaelBg: FALLBACK_BG[bit], skill, art };
}

/** 영웅에 맞는 아이콘으로 바꾼다(마을에서 영웅을 바꿀 때·버튼 생성 때). 라이라 글리프가 없는 버튼(회피)은 그대로 */
export function setFaceHero(f: SkillFace, hero: PlayableHeroId, job: 0 | 1 = 0): void {
  if (f.art) {
    f.art.set(hero, f.skill, (hero === 'lyra' ? LYRA_ICONS : SKILL_ICONS)[SKILL_BTN_ALL[f.skill - 1]] ?? '', job);
    return;
  }
  if (!f.lyraLayer) return;
  const lyra = hero === 'lyra';
  f.lyraLayer.style.display = lyra ? 'block' : 'none';
  f.kaelLayer.style.display = lyra ? 'none' : 'block';
  f.face.style.background = lyra ? LYRA_FACE_BG : f.kaelBg;
}

/** 시계 바늘 반짝임(c017): 쐐기가 한 바퀴(340ms) 돌며 사라진다 — 재사용 대기가 앞당겨졌다는 신호. 버튼 안에서만(화면 섬광 아님) */
export const GLINT_KF: Keyframe[] = [
  { opacity: 0, transform: 'rotate(0deg)' },
  { opacity: 1, transform: 'rotate(40deg)', offset: 0.12 },
  { opacity: 0.85, transform: 'rotate(300deg)', offset: 0.8 },
  { opacity: 0, transform: 'rotate(360deg)' },
];
/** 움직임 줄이기(prefers-reduced-motion)·섬광 약화(게임 설정): 돌지 않고 12시 자리에서 옅게 한 번 켜졌다 꺼진다 */
export const GLINT_STILL_KF: Keyframe[] = [{ opacity: 0 }, { opacity: 0.7, offset: 0.3 }, { opacity: 0 }];
export const GLINT_OPT: KeyframeAnimationOptions = { duration: 340, easing: 'cubic-bezier(.2,.6,.3,1)' };
/**
 * 같은 칸 반짝임 최소 간격(ms): 공용 섬광 규칙(FLASH_SAFETY 간격 334ms)과 같다 — 치명타가 연달아 터져도 한 칸이 1초에 3번을 넘지 않는다(PR #161 리뷰 P1)
 */
export const GLINT_GAP_MS = FLASH_SAFETY.minGapMs;
const REDUCED_MOTION = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
/** 움직임 줄이기 설정(브라우저) */
export function reducedMotion(): boolean { return REDUCED_MOTION !== null && REDUCED_MOTION.matches; }

/** 버튼 하나에 시계 바늘 반짝임을 건다(간격 안이면 건너뛴다). reduceFlash = 게임 설정 섬광 약화(돌지 않는 옅은 켜짐). 반환: 걸었으면 true */
export function glintFace(f: SkillFace, nowMs: number, reduceFlash = false): boolean {
  if (nowMs - f.glintAt < GLINT_GAP_MS) return false;
  f.glintAt = nowMs;
  f.glint.animate(reduceFlash || reducedMotion() ? GLINT_STILL_KF : GLINT_KF, GLINT_OPT);
  return true;
}

/** Ev.GearUnique가 '칼날의 박자'(치명타 재사용 감소)인지 */
export function isRefundCue(e: GameEvent): boolean { return e.type === Ev.GearUnique && uniqueCueOf(e.a) === GearCue.Refund; }

/** 캔슬 표시 켜기/끄기(PC 바·터치 공용). 호출자가 직전 값과 비교해 바뀔 때만 부른다 */
export function setCancelMark(f: SkillFace, on: boolean): void {
  const d = on ? 'block' : 'none';
  f.cancel.style.display = d;
  f.cancelTag.style.display = d;
}

// ── 미리 만든 문자열 표 ─────────────────────────────────
const SWEEP_STEPS = 100;
/** 남은 비율 q%만큼 어둡게 덮는 원형 스윕(시계 방향으로 걷힌다). q=0이면 'none' */
export const CD_SWEEP: readonly string[] = Array.from({ length: SWEEP_STEPS + 1 }, (_, q) =>
  q === 0 ? 'none' : 'conic-gradient(rgba(6,8,18,.72) ' + q + '%,rgba(0,0,0,0) 0)');
/** 각성 게이지: 채운 비율 q%를 제외한 나머지를 어둡게 덮는다 */
export const AW_SWEEP: readonly string[] = Array.from({ length: SWEEP_STEPS + 1 }, (_, q) =>
  q >= SWEEP_STEPS ? 'none' : 'conic-gradient(rgba(0,0,0,0) ' + q + '%,rgba(6,8,18,.62) 0)');
/** 남은 시간(0.1초 단위) → 표시 문자열. 1초 이상은 올림 정수, 미만은 소수 한 자리 */
// 전직 스킬(카엘 9·10·라이라 12·13, 쿨 18초)까지 넣는다(빠지면 18초 쿨의 처음 몇 초가 상한에 잘려 보였다)
const MAX_TENTHS = Math.ceil(Math.max(...[...KAEL_SKILL_SPECS_BY_JOB, ...LYRA_SKILL_SPECS_BY_JOB].flatMap((t) => t.map((s) => s.cooldown))) / 6) + 1;
export const CD_TEXT: readonly string[] = Array.from({ length: MAX_TENTHS + 1 }, (_, t) =>
  t === 0 ? '' : t >= 10 ? String(Math.ceil(t / 10)) : '0.' + t);
/** 남은 틱 → CD_TEXT 인덱스(0.1초 단위, 올림) */
export function cdTenths(ticks: number): number {
  return ticks <= 0 ? 0 : Math.min(MAX_TENTHS, Math.ceil(ticks / 6));
}
/** 남은 비율 → CD_SWEEP 인덱스 */
export function sweepStep(left: number, max: number): number {
  return left > 0 ? Math.max(1, Math.min(SWEEP_STEPS, Math.ceil((left / (max || 1)) * SWEEP_STEPS))) : 0;
}

const STYLE_ID = 'dh-skillbar-style';
const CSS =
  '@keyframes dhSkReady{0%,100%{box-shadow:0 0 8px 2px rgba(255,60,60,.6)}50%{box-shadow:0 0 26px 9px rgba(255,120,60,.95)}}' +
  // 캔슬 고리 맥동: 버튼 둘레의 금색 빛만 약 0.8초 주기로 커졌다 작아진다(화면 섬광이 아니다). 동작 줄이기면 정지한 고리
  '@keyframes dhSkCancel{0%,100%{box-shadow:0 0 6px 1px rgba(255,200,70,.5),inset 0 0 5px rgba(255,214,110,.35)}50%{box-shadow:0 0 16px 5px rgba(255,214,90,.9),inset 0 0 9px rgba(255,224,140,.6)}}' +
  '@media (prefers-reduced-motion:reduce){.dh-sk-cancel{animation:none!important;box-shadow:0 0 10px 3px rgba(255,214,90,.75)}}';
/** 캔슬 고리 맥동 애니메이션 값과 꼬리표(게임 화면의 목표 이미지: 어두운 남색 판 + 금테 + 금색 글자) */
const CANCEL_ANIM = 'dhSkCancel .8s ease-in-out infinite';
const CANCEL_TEXT = 'CANCEL';
const CANCEL_TAG_CSS = 'position:absolute;left:50%;top:calc(100% - 22px);transform:translateX(-50%);display:none;pointer-events:none;z-index:3;white-space:nowrap;' +
  'padding:0 6px;height:15px;line-height:14px;box-sizing:border-box;border:1.5px solid ' + C.gold + ';border-radius:4px;background:linear-gradient(180deg,#26335a,#10162a);' +
  'font:900 10px ' + FONT_NUM + ';letter-spacing:.8px;color:#ffe07a;text-shadow:0 1px 1px #000;box-shadow:0 2px 3px rgba(0,0,0,.7);';

/** 스타일 시트를 한 번만 넣는다(PC 바·터치 버튼 모두 buildSkillFace에서 부른다) */
function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent = CSS;
  document.head.appendChild(st);
}
const POP_KF: Keyframe[] = [{ transform: 'scale(1.14)', filter: 'brightness(1.6)' }, { transform: 'scale(1)', filter: 'brightness(1)' }];
const POP_OPT: KeyframeAnimationOptions = { duration: 170, easing: 'ease-out' };
/** 준비 반짝임: 아이콘 둘레 링만(화면 섬광이 아니다) */
export const READY_KF: Keyframe[] = [
  { opacity: 0, transform: 'scale(.9)' },
  { opacity: 1, transform: 'scale(1.04)', offset: 0.35 },
  { opacity: 0, transform: 'scale(1.22)' },
];
export const READY_OPT: KeyframeAnimationOptions = { duration: 420, easing: 'ease-out' };
const DENY_KF: Keyframe[] = [
  { transform: 'translateX(0)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' },
  { transform: 'translateX(-3px)' }, { transform: 'translateX(0)' },
];
const DENY_OPT: KeyframeAnimationOptions = { duration: 220, easing: 'linear' };
const DENY_MP_KF: Keyframe[] = [{ transform: 'scale(1.4)' }, { transform: 'scale(1)' }];

export const DIM_FILTER = 'grayscale(.6) brightness(.45)';
export const AW_READY_ANIM = 'dhSkReady 1.2s ease-in-out infinite';
const PRESSED = 'scale(.9)';
const RELEASED = 'scale(1)';
const PIP_ON = C.gold;
const PIP_OFF = 'rgba(255,255,255,.3)';
/** MP 표시 색: 정상 / 부족 */
export const MP_OK_CSS = 'background:linear-gradient(180deg,#2a63c4,#123a86);border-color:#8ac2ff;color:#fff;';
const MP_OK_BG = 'linear-gradient(180deg,#2a63c4,#123a86)';
const MP_SHORT_BG = 'linear-gradient(180deg,#5a1018,#2a060c)';

/** 키 → 슬롯 순서(공격·U·I·O·L·P·회피·각성). src/core/input.ts의 키 배정과 같다 */
// c022: 스킬 버튼은 스킬 번호 1..SKILL_MAX(12)를 모두 만들어 두고(영웅마다 이름·MP만 바꾼다) 회피·각성이 그 뒤에 온다
const SLOT_DODGE = SKILL_MAX + 1, SLOT_AWAKEN = SKILL_MAX + 2;
const KEY_SLOT: Readonly<Record<string, number>> = { KeyJ: 0, KeyU: 1, KeyI: 2, KeyO: 3, KeyL: 4, KeyP: 5, KeyK: SLOT_DODGE, ShiftLeft: SLOT_DODGE, ShiftRight: SLOT_DODGE, Space: SLOT_AWAKEN };
/** 슬롯 숫자 키(윗줄·숫자패드 1~6) */
const DIGIT_KEY = /^(?:Digit|Numpad)([1-7])$/;

/** MP 숫자 표시 상태를 바꾼다(PC·터치 공용) */
export function setMpBadge(mp: HTMLDivElement, ok: boolean): void {
  mp.style.background = ok ? MP_OK_BG : MP_SHORT_BG;
  mp.style.borderColor = ok ? '#8ac2ff' : '#ff5a5a';
  mp.style.color = ok ? '#fff' : '#ff6a6a';
}

interface Slot {
  bit: BtnBit;
  wrap: HTMLDivElement;
  f: SkillFace;
  mpText: HTMLDivElement | null;
  pips: HTMLDivElement[];
  /** 스킬 번호 1..5, 공격·회피·각성은 0 */
  skill: number;
  mpCost: number;
  lastSweep: number;
  lastTenths: number;
  lastMpOk: number;
  lastReady: number;
  lastCancel: number;
  lastPip: number;
  pressedBy: number;
  /** 습득 전이거나 현재 페이지에 없는 스킬은 보이지 않는다(숨김이면 입력·갱신을 건너뛴다) */
  hidden: boolean;
  /** 키 표시(왼쪽 위). 스킬은 슬롯 번호(1~6), 그 밖은 키 이름 */
  badge: HTMLDivElement;
  /** 옛 키 글자(U·I·O·L·P)와 이름: 접근성 이름에 슬롯 번호와 함께 남긴다 */
  key: string;
  name: string;
  /** 이름 글자(영웅이 바뀌면 다시 쓴다, c022) */
  nameEl: HTMLDivElement;
}

/** PC 전용: 각성 게이지를 포함한 높이 92px + 아래 여백 12px를 지킨다. 반올림으로 경계를 넘지 않게 내린다. */
export function pcSkillBarScale(u: number, screenW: number, width: number, height: number): number {
  return Math.floor(Math.min(u, (screenW - 16) / Math.max(1, width), 92 / Math.max(96, height)) * 1000) / 1000;
}

export class SkillBar {
  readonly root: HTMLDivElement;
  /** 각성 게이지(HUD 소유)를 옮겨 붙이는 자리: 슬롯 줄 바로 위 */
  readonly gaugeSlot: HTMLDivElement;
  private readonly slots: Slot[] = [];
  private shown = false;
  private lastScale = -1;
  private lastWinW = -1;
  /** 눌려 있는 키 → 그 키가 누른 슬롯 인덱스(슬롯 키는 눌린 순간의 배치로 정한다) */
  private readonly keysDown = new Map<string, number>();
  /** 쪽 표시 칩(1/2 · Tab · 다음 스킬)과 칸 자리표시(빈 칸 6개) */
  private readonly chip: HTMLDivElement;
  private readonly chipPage: HTMLSpanElement;
  /** 쪽 표시 줄(1/2 · Tab): 2쪽이 비어 있으면 감춘다(pageSwitchOpen) */
  private readonly chipTop: HTMLDivElement;
  private pageOpen = true;
  private readonly chipNext: HTMLDivElement;
  private readonly cells: HTMLDivElement[] = [];
  private lastLayout = -1;
  private lastNextLv = -1;
  /** 버튼 이름·MP를 맞춘 영웅(c022). 바뀌면 relabel */
  private builtHero: PlayableHeroId = 'kael';
  /** 마지막으로 그린 전직 갈래(C10-6: 전직 스킬 이름·그림) */
  private builtJob: 0 | 1 = 0;
  /** 물약 퀵슬롯(D2c): 바 맨 왼쪽 칸(R·T) */
  private potions: PotionHud | null = null;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly input?: InputSink, private readonly send?: (c: GameCommand) => void) {
    if (!document.getElementById(STYLE_ID)) {
      const st = document.createElement('style');
      st.id = STYLE_ID;
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    // 아래 여백 20px: 4px이면 버튼 이름이 화면 아래 끝에 붙었다(VFX/룩 dream-loop 보스 장면 지적, 목표 이미지는 화면 높이의 약 7%)
    this.root = el('div', 'position:absolute;left:50%;bottom:max(12px,env(safe-area-inset-bottom));transform-origin:50% 100%;display:none;flex-direction:column;align-items:center;gap:2px;pointer-events:none;font-family:' + FONT + ';', parent);
    this.root.dataset.testid = 'skillbar';
    this.gaugeSlot = el('div', 'display:flex;justify-content:center;', this.root);
    // 바닥: 테두리 없는 어두운 그림자(아이콘·링이 주인공)
    const row = el(
      'div',
      'display:flex;align-items:flex-end;gap:6px;padding:2px 6px;background:radial-gradient(ellipse 50% 100% at 50% 100%,rgba(8,10,22,.78) 0%,rgba(8,10,22,.45) 55%,rgba(8,10,22,0) 100%);',
      this.root,
    );
    // 쪽 표시 칩(맨 왼쪽): 1/2 · Tab · 다음 스킬 Lv. 누르면 페이지가 바뀐다
    this.chip = el('div', 'order:0;align-self:flex-end;margin-bottom:0;min-height:44px;min-width:56px;justify-content:center;box-sizing:border-box;padding:3px 8px 4px;border-radius:9px;border:1.5px solid ' + C.gold + ';' +
      'background:linear-gradient(180deg,rgba(38,51,90,.92),rgba(16,22,42,.92));box-shadow:0 2px 4px rgba(0,0,0,.6);display:flex;flex-direction:column;align-items:center;gap:1px;' +
      'pointer-events:auto;cursor:pointer;touch-action:manipulation;user-select:none;white-space:nowrap;', row);
    this.chip.dataset.testid = 'skill-page';
    if (this.input) this.potions = new PotionHud(row, view, this.input, 'pc');
    this.chip.setAttribute('role', 'button');
    const top = el('div', 'display:flex;align-items:center;gap:5px;', this.chip);
    this.chipTop = top as HTMLDivElement;
    this.chipPage = el('span', 'font:900 15px ' + FONT_NUM + ';letter-spacing:.5px;color:#ffe07a;' + TEXT_OUTLINE, top, pageText(0));
    el('span', 'font:800 10px ' + FONT + ';color:' + C.gold + ';border:1px solid ' + C.gold + ';border-radius:4px;padding:0 4px;line-height:13px;', top, PAGE_KEY_HINT);
    this.chipNext = el('div', 'font:700 10.5px ' + FONT + ';color:#cfd6ee;display:none;', this.chip, '');
    this.chipNext.dataset.testid = 'next-skill';
    this.chip.addEventListener('pointerdown', (e) => e.preventDefault());
    this.chip.addEventListener('click', () => { if (this.pageOpen) this.send?.({ type: 'setSkillPage' }); });
    this.builtHero = view.heroId ?? 'kael'; this.builtJob = view.job?.branch === 1 ? 1 : 0;
    this.make(row, Btn.Attack, 'J', '공격', 62, 0, 0).wrap.style.order = '5';
    // 칸 자리표시: 빈 칸(습득 전·비운 칸)에 점선 원과 칸 번호를 그려 6칸이 한눈에 보이게 한다
    // 7번째 칸(c = 6)은 환생 추가 칸: 열렸을 때만 자리표시가 보인다(레인 R)
    for (let c = 0; c <= SLOTS_PER_PAGE; c++) {
      const cell = el('div', 'order:' + (10 + c * 2) + ';width:56px;height:56px;box-sizing:border-box;margin-bottom:0;border-radius:7px;border:1px dashed rgba(242,193,78,.62);background:rgba(8,10,22,.62);' +
        'display:flex;align-items:center;justify-content:center;pointer-events:none;font:800 13px ' + FONT_NUM + ';color:rgba(255,224,122,.85);text-shadow:0 1px 2px #000;', row, String(c + 1));
      cell.dataset.testid = 'slot-empty-' + (c + 1);
      this.cells.push(cell);
    }
    for (let i = 0; i < SKILL_MAX; i++) {
      const sp = skillSpecOf(i + 1, this.builtHero, this.builtJob);
      // 습득(Lv5 천공 올려베기·Lv10 진홍 초승달 자동 습득)하고 현재 페이지에 놓인 스킬만 보인다(미구현·미습득 스킬 UI 노출 금지). 처음엔 숨기고 applyLayout이 켠다.
      // 스킬 번호 칸은 12개 모두 만든다(영웅마다 스킬 수가 달라, 없는 번호는 습득 마스크가 0이라 늘 숨는다)
      this.make(row, SKILL_BTN_ALL[i], sp?.key ?? '', sp?.name ?? '', 56, i + 1, sp?.mpCost ?? 0, true);
    }
    // 회피 키 표시는 키 배치를 따른다(액션식 K, PC RPG식 Shift — shared/keymap.ts). 바꾸면 그 자리에서 고친다
    const dodge = this.make(row, Btn.Dodge, dodgeKeyLabel(keymap.preset), '회피', 52, 0, 0);
    dodge.wrap.style.order = '40';
    keymap.subscribe((p) => {
      const k = dodgeKeyLabel(p);
      dodge.key = k; dodge.badge.textContent = k; dodge.badge.style.fontSize = (k.length > 1 ? 8 : 11) + 'px';
      dodge.wrap.setAttribute('aria-label', '회피 (' + k + ')'); dodge.wrap.title = '회피 (' + k + ')';
    });
    this.make(row, Btn.Awaken, 'SPACE', '각성', 62, 0, 0).wrap.style.order = '41';
    this.applyLayout();
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.root.remove();
  }

  private make(row: HTMLElement, bit: BtnBit, key: string, name: string, size: number, skill: number, mpCost: number, hidden = false): Slot {
    const wrap = el('div', 'position:relative;display:' + (hidden ? 'none' : 'flex') + ';flex-direction:column;align-items:center;gap:0;pointer-events:auto;cursor:pointer;transition:transform 60ms ease-out;touch-action:none;user-select:none;', row);
    wrap.dataset.testid = 'skill-' + bit;
    wrap.setAttribute('role', 'button');
    wrap.setAttribute('aria-label', name + ' (' + key + ')' + (skill > 0 ? ' · MP ' + mpCost : ''));
    wrap.title = name + ' (' + key + ')' + (skill > 0 ? ' · MP ' + mpCost : '');
    const f = buildSkillFace(wrap, bit, 'width:' + size + 'px;');
    setFaceHero(f, this.builtHero, this.builtJob);
    f.cdText.style.fontSize = Math.round(size * 0.3) + 'px';
    // 키 표시(왼쪽 위, 링 장식에 걸치게)
    const badge = el('div', 'position:absolute;left:' + (bit === Btn.Awaken ? 8 : 2) + 'px;top:' + (bit === Btn.Awaken ? 10 : 2) + 'px;min-width:19px;height:19px;padding:0 4px;box-sizing:border-box;border-radius:5px;' +
      'background:linear-gradient(180deg,#26335a,#10162a);border:1.5px solid ' + C.gold + ';box-shadow:0 2px 3px rgba(0,0,0,.6);' +
      'font:800 ' + (key.length > 1 ? 8 : 11) + 'px ' + FONT + ';color:' + C.gold + ';display:flex;align-items:center;justify-content:center;pointer-events:none;letter-spacing:.5px;z-index:2;', f.root, key);
    let mpText: HTMLDivElement | null = null;
    if (mpCost > 0 || skill > 0) {
      mpText = el('div', 'position:absolute;right:-4px;bottom:4%;padding:1px 6px;border-radius:8px;border:1.5px solid #8ac2ff;' + MP_OK_CSS +
        'font:900 13px ' + FONT_NUM + ';line-height:1;letter-spacing:.5px;box-shadow:0 2px 3px rgba(0,0,0,.7);text-shadow:0 1px 1px #000;pointer-events:none;z-index:2;', f.root, 'MP');
      mpText.style.display = 'none';
    }
    const pips: HTMLDivElement[] = [];
    if (bit === Btn.Attack) {
      const pr = el('div', 'position:absolute;left:50%;bottom:16%;transform:translateX(-50%);display:flex;gap:3px;pointer-events:none;', f.face);
      for (let i = 0; i < 4; i++) pips.push(el('div', 'width:6px;height:6px;border-radius:50%;box-shadow:0 0 2px #000;background:' + PIP_OFF + ';', pr));
    }
    const nameEl = el('div', (skill > 0 ? 'display:none;' : 'position:absolute;bottom:3px;left:0;right:0;text-align:center;') + 'margin-top:0;font:800 12px ' + FONT + ';color:#fff;' + TEXT_OUTLINE + 'white-space:nowrap;pointer-events:none;letter-spacing:.5px;', wrap, name);
    const slot: Slot = { bit, wrap, f, mpText, pips, skill, mpCost, lastSweep: -1, lastTenths: -1, lastMpOk: -1, lastReady: -1, lastCancel: 0, lastPip: -2, pressedBy: -1, hidden, badge, key, name, nameEl };
    if (this.input) {
      // 마우스로 슬롯을 눌러도 같은 입력이 나간다(터치 버튼과 같은 InputSink 경로)
      const sink = this.input;
      wrap.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (slot.hidden || slot.pressedBy !== -1) return;
        slot.pressedBy = e.pointerId;
        wrap.setPointerCapture(e.pointerId);
        this.press(slot);
        sink.buttonDown(bit);
      });
      const up = (e: PointerEvent): void => {
        if (e.pointerId !== slot.pressedBy) return;
        slot.pressedBy = -1;
        this.release(slot);
        sink.buttonUp(bit);
      };
      wrap.addEventListener('pointerup', up);
      wrap.addEventListener('pointercancel', up);
    }
    this.slots.push(slot);
    return slot;
  }

  /** 지금 누르면 실제로 나가는지(쿨타임·MP·게이지) */
  private available(s: Slot): boolean {
    const h = this.view.hero;
    if (s.skill > 0) return !s.hidden && h.cooldowns[s.skill - 1] <= 0 && h.mp >= effectiveMpCost(s.mpCost, h.mpCostCut);
    if (s.bit === Btn.Awaken) return h.awakenGauge >= 1;
    if (s.bit === Btn.Dodge) return h.dodgeCooldown <= 0;
    return true;
  }

  /** 눌림 반응. 못 쓰는 상태면 흔들림, MP 부족이면 MP 숫자가 튄다 */
  private press(s: Slot): void {
    s.wrap.style.transform = PRESSED;
    if (!this.shown || this.available(s)) return;
    s.f.root.animate(DENY_KF, DENY_OPT);
    if (s.mpText && this.view.hero.mp < effectiveMpCost(s.mpCost, this.view.hero.mpCostCut)) s.mpText.animate(DENY_MP_KF, POP_OPT);
  }

  private release(s: Slot): void {
    s.wrap.style.transform = RELEASED;
  }

  /** 키 → 슬롯 인덱스: 옛 키는 고정 표, 숫자 키(1~6·Alt+1~6)는 눌린 순간의 슬롯 배치가 가리키는 스킬 버튼 */
  private slotOfKey(e: KeyboardEvent): number | undefined {
    // PC RPG식 배치에서 K는 스킬 창 키라 회피 버튼을 누르지 않는다(core/input.ts와 같은 판정)
    const fixed = e.code === 'KeyK' && keymap.preset !== 'action' ? undefined : KEY_SLOT[e.code];
    if (fixed !== undefined) return fixed;
    const m = DIGIT_KEY.exec(e.code);
    if (!m || e.ctrlKey || e.metaKey) return undefined;
    const sv = slotsOf(this.view);
    const bit = slotBtn(slotsAll(sv), keySlot(sv.page, Number(m[1]) - 1, e.altKey));
    if (bit === 0) return undefined;
    const i = this.slots.findIndex((sl) => sl.bit === bit);
    return i >= 0 ? i : undefined;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const i = this.slotOfKey(e);
    if (i === undefined || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    if (this.slots[i].hidden) return;
    this.keysDown.set(e.code, i);
    this.press(this.slots[i]);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    const i = this.keysDown.get(e.code);
    if (i === undefined) return;
    this.keysDown.delete(e.code);
    this.release(this.slots[i]);
  };

  private onBlur = (): void => {
    this.keysDown.clear();
    for (const s of this.slots) this.release(s);
  };

  /**
   * 슬롯 배치·페이지·습득 상태에 맞춰 버튼 표시·순서·칸 번호를 다시 맞춘다(값이 바뀔 때만 부른다).
   * 스킬 버튼은 습득했고 현재 페이지에 놓였을 때만 보이며 슬롯 번호 순서(1~6)로 늘어선다. 안 보이는 칸은 점선 자리표시가 채운다.
   */
  private applyLayout(): void {
    const g = growthOf(this.view);
    const learned = learnedMaskOf(g);
    const sv = slotsOf(this.view);
    this.lastLayout = layoutKey(sv, learned);
    let filled = 0; // 채워진 칸 비트마스크(칸 6개 — 배열을 새로 만들지 않는다)
    for (const s of this.slots) {
      if (s.skill === 0) continue;
      const cell = cellOfSkill(sv, s.skill);
      const on = skillShown(sv, learned, s.skill);
      if (on === s.hidden) {
        s.hidden = !on;
        s.wrap.style.display = on ? 'flex' : 'none';
        if (!on && s.pressedBy !== -1) { s.pressedBy = -1; this.release(s); this.input?.buttonUp(s.bit); }
        // 처음 보이는 순간 준비 반짝임이 터지지 않게 상태를 처음으로 되돌린다
        s.lastSweep = s.lastTenths = s.lastMpOk = s.lastReady = -1;
      }
      if (on) {
        filled |= 1 << cell;
        s.wrap.style.order = String(10 + cell * 2);
        s.badge.textContent = String(cell + 1);
        s.badge.style.font = '800 11px ' + FONT;
        // 슬롯 키는 숫자(1~7). 옛 글자 키(U·I·O·L·P)는 스킬 번호에 묶여 있어 7번 칸 등에서는 엉뚱하게 보이므로 안내하지 않는다
        s.wrap.setAttribute('aria-label', s.name + ' (슬롯 ' + (cell + 1) + ' · 키 ' + (cell + 1) + ') · MP ' + s.mpCost);
        s.wrap.title = s.name + ' · MP ' + s.mpCost + ' · 슬롯 ' + (cell + 1) + ' (키 ' + (cell + 1) + ')';
      }
    }
    const cellN = SLOTS_PER_PAGE + ((sv.extra ?? 0) > 0 ? 1 : 0);
    for (let c = 0; c < this.cells.length; c++) this.cells[c].style.display = c >= cellN || (filled >> c) & 1 ? 'none' : 'flex';
    this.chipPage.textContent = pageText(sv.page);
    this.pageOpen = pageSwitchOpen(sv, learned);
    this.chipTop.style.display = this.pageOpen ? 'flex' : 'none';
    this.chip.setAttribute('aria-label', '스킬 슬롯 ' + pageText(sv.page) + '쪽, 누르면 다음 쪽 (' + PAGE_KEY_HINT + ')');
  }

  /** 영웅이 바뀌었다(c022, 마을에서만): 스킬 버튼 이름·MP·키 글자를 그 영웅 표로 다시 쓴다(버튼 수·순서는 그대로) */
  private relabel(hero: PlayableHeroId, job: 0 | 1): void {
    this.builtHero = hero; this.builtJob = job;
    for (const s of this.slots) {
      setFaceHero(s.f, hero, job);
      if (s.skill === 0) continue;
      const sp = skillSpecOf(s.skill, hero, job);
      s.name = sp?.name ?? ''; s.key = sp?.key ?? ''; s.mpCost = sp?.mpCost ?? 0;
      s.nameEl.textContent = s.name;
      if (s.mpText) s.mpText.textContent = 'MP';
      s.lastSweep = s.lastTenths = s.lastMpOk = s.lastReady = -1;
    }
    this.lastLayout = -1; this.lastNextLv = -1;
  }

  private setNextText(text: string): void {
    this.chipNext.textContent = text;
    this.chipNext.style.display = 'none';
    this.chip.title = 'Tab · 페이지 전환' + (text ? ' · ' + text : '');
  }

  private pop(i: number): void {
    if (this.shown && !this.slots[i].hidden) this.slots[i].f.root.animate(POP_KF, POP_OPT);
  }

  /** HUD가 읽은 게임 이벤트를 넘겨받아 실제로 나간 동작에 반응한다. */
  onEvent(e: GameEvent): void {
    if (e.type === Ev.SkillCast) { if (e.a >= 1 && e.a <= SKILL_MAX) this.pop(e.a); }
    else if (e.type === Ev.Dodge) this.pop(SLOT_DODGE);
    else if (e.type === Ev.Slash) { if (e.b < 10) this.pop(0); }
    else if (e.type === Ev.AwakenPhase) { if (e.a === AwakenPhase.Gather) this.pop(SLOT_AWAKEN); }
    else if (isRefundCue(e)) this.glintCooling();
  }

  /** 칼날의 박자: 지금 재사용 대기 중인(보이는) 스킬 칸마다 시계 바늘 반짝임 */
  private glintCooling(): void {
    if (!this.shown) return;
    const h = this.view.hero, now = performance.now();
    for (let n = 0; n < this.slots.length; n++) {
      const s = this.slots[n];
      if (s.skill > 0 && !s.hidden && h.cooldowns[s.skill - 1] > 0) glintFace(s.f, now, this.view.settings.reduceFlash);
    }
  }

  /** u: 디자인 해상도 배율. visible: 던전 전투 중일 때만 보인다. */
  update(u: number, visible: boolean): void {
    if (visible !== this.shown) {
      this.shown = visible;
      this.root.style.display = visible ? 'flex' : 'none';
    }
    this.potions?.update(visible);
    if (!visible) return;
    const h = this.view.hero;
    // 캔슬 가능 비트(sim이 쿨·MP·시점을 모두 반영해 내보낸다). 바뀔 때만 DOM을 쓴다
    const link = this.view.link;
    const cancelBits = link ? link.cancelReady : 0;
    // 습득 마스크(레벨 자동 습득 + 트리 랭크)와 슬롯 배치·페이지가 바뀔 때만 버튼 표시·순서를 다시 맞춘다
    const hero = this.view.heroId ?? 'kael', job: 0 | 1 = this.view.job?.branch === 1 ? 1 : 0;
    // 영웅·전직 갈래(전직 스킬 이름·그림이 갈래마다 다르다, C10-6)가 바뀌면 다시 쓴다
    if (hero !== this.builtHero || job !== this.builtJob) this.relabel(hero, job);
    const g = growthOf(this.view);
    const learned = learnedMaskOf(g);
    const sv = slotsOf(this.view);
    const lk = layoutKey(sv, learned);
    const relayout = lk !== this.lastLayout;
    if (relayout) this.applyLayout();
    // 배율: 디자인 배율 u, 단 화면 폭을 넘지 않게 줄인다(칩·6칸이 생겨 바가 넓어졌다. 세로 화면의 PC 창에서 각성 버튼이 잘렸다). 폭이 바뀔 때만 레이아웃 폭을 잰다
    const winW = viewW();
    if (u !== this.lastScale || relayout || winW !== this.lastWinW) {
      this.lastScale = u;
      this.lastWinW = winW;
      const bw = this.root.offsetWidth;
      const f = pcSkillBarScale(u, winW, bw, this.root.offsetHeight);
      this.root.style.transform = 'translateX(-50%) scale(' + f.toFixed(3) + ')';
    }
    const nl = g ? g.level : 0;
    if (nl !== this.lastNextLv) { this.lastNextLv = nl; this.setNextText(g ? nextSkillText(g.level, hero) : ''); }
    for (let n = 0; n < this.slots.length; n++) {
      const s = this.slots[n];
      if (s.skill > 0 && s.hidden) continue;
      const cancelOn = s.skill > 0 ? (cancelBits >> (s.skill - 1)) & 1 : 0;
      if (cancelOn !== s.lastCancel) { s.lastCancel = cancelOn; setCancelMark(s.f, cancelOn === 1); }
      let sweep: number, tenths = 0, mpOk = 1, ready: number;
      if (s.skill > 0) {
        const i = s.skill - 1;
        sweep = sweepStep(h.cooldowns[i], h.cooldownMax[i]);
        tenths = cdTenths(h.cooldowns[i]);
        mpOk = h.mp >= effectiveMpCost(s.mpCost, h.mpCostCut) ? 1 : 0;
        ready = sweep === 0 && mpOk === 1 ? 1 : 0;
      } else if (s.bit === Btn.Dodge) {
        sweep = sweepStep(h.dodgeCooldown, DODGE.cooldown);
        ready = sweep === 0 ? 1 : 0;
      } else if (s.bit === Btn.Awaken) {
        const g = Math.max(0, Math.min(1, h.awakenGauge));
        sweep = Math.floor(g * SWEEP_STEPS);
        ready = g >= 1 ? 1 : 0;
      } else {
        sweep = 0;
        ready = 1;
        const pip = h.act === HeroAct.Attack ? h.comboStep : -1;
        if (pip !== s.lastPip) {
          s.lastPip = pip;
          for (let k = 0; k < s.pips.length; k++) s.pips[k].style.background = k <= pip ? PIP_ON : PIP_OFF;
        }
      }
      if (sweep !== s.lastSweep) {
        s.lastSweep = sweep;
        // 각성은 채워지지 않은 부분만 어둡게(퍼센트는 바로 위 게이지 줄에 있다)
        s.f.sweep.style.background = s.bit === Btn.Awaken ? AW_SWEEP[sweep] : CD_SWEEP[sweep];
      }
      if (tenths !== s.lastTenths && s.bit !== Btn.Awaken) {
        s.lastTenths = tenths;
        s.f.cdText.textContent = CD_TEXT[tenths];
      }
      if (mpOk !== s.lastMpOk) {
        s.lastMpOk = mpOk;
        s.f.face.style.filter = mpOk ? '' : DIM_FILTER;
        s.wrap.dataset.mp = mpOk ? 'ok' : 'low';
        if (s.mpText) { setMpBadge(s.mpText, mpOk === 1); s.mpText.style.display = mpOk ? 'none' : 'block'; }
      }
      if (ready !== s.lastReady) {
        // 준비되는 순간(처음 표시 제외) 짧은 링 반짝임. 각성은 준비 상태 동안 은은한 맥박(1.2초 주기)
        if (ready === 1 && s.lastReady === 0 && s.bit !== Btn.Attack) s.f.flash.animate(READY_KF, READY_OPT);
        s.lastReady = ready;
        s.wrap.dataset.ready = String(ready); s.wrap.dataset.mp = mpOk ? 'ok' : 'low';
        if (s.bit === Btn.Awaken) s.f.cdText.textContent = ready ? '준비' : '';
        if (s.bit === Btn.Awaken) s.f.face.style.animation = ready ? AW_READY_ANIM : 'none';
      }
    }
  }
}

